/**
 * Lo apartado: the stock pending orders set aside (the owner, 2026-10-08: «we need stock
 * reservation by both orders and the till», with `apartarPedidos` on).
 *
 * Nothing is stored for it. A pending order is what sets its lines aside, in base units, in the
 * shop, where every till sells from since 2026-10-05; delivered or cancelled, it no longer does.
 * So every device and the server agree on it as soon as they hold the same orders, and the order
 * the server puts the log in decides, once, which of two orders for the last units came first:
 * the second is refused (`pedido.tomado`), on every device alike.
 *
 * A sale is never refused for it: the goods have left. The till asks the owner first; a sale made
 * anyway, by a phone that had not heard of the order yet, leaves that order short, and `cobertura`
 * says which, the orders taken first served first.
 */

import { ZERO_QTY, addQty, fromUnits, saleToBase, subQty, type Milli } from '@abastece/shared';
import type { Pedido, Reader } from './state.js';

/** Where orders set stock aside: the shop's floor, where the tills sell from. */
export const DONDE_SE_APARTA = 'tienda';

/** A presentation's factor, by its id: an order line keeps the presentation, not the factor. */
export type FactorDe = (unidadVentaId: string) => Milli | undefined;

/** Each presentation's factor, read off the books once for all of them. */
export async function factoresEnLibros(read: Pick<Reader, 'unidadVenta'>, ids: Iterable<string>): Promise<FactorDe> {
  const factores = new Map<string, Milli>();
  for (const id of new Set(ids)) {
    const unidad = await read.unidadVenta(id);
    if (unidad) factores.set(id, unidad.factor);
  }
  return (id) => factores.get(id);
}

/** What each line of an order takes, by product, in base units; a presentation not found counts as one unit. */
export function loQuePide(lineas: readonly Pick<Pedido['lineas'][number], 'productoId' | 'unidadVentaId' | 'cantidad'>[], factorDe: FactorDe): Map<string, Milli> {
  const pide = new Map<string, Milli>();
  for (const l of lineas) {
    const base = saleToBase(l.cantidad, factorDe(l.unidadVentaId) ?? fromUnits(1));
    pide.set(l.productoId, addQty(pide.get(l.productoId) ?? ZERO_QTY, base));
  }
  return pide;
}

/** Order of service: the order taken first is served first; the same day, the one the log has first. */
export const primeroElPrimero = (a: Pick<Pedido, 'fecha' | 'rev' | 'id'>, b: Pick<Pedido, 'fecha' | 'rev' | 'id'>): number =>
  a.fecha.localeCompare(b.fecha) || a.rev - b.rev || a.id.localeCompare(b.id);

/** What the pending orders set aside, by product, in base units; `excepto` leaves one order out. */
export function apartadoPorProducto(pedidos: readonly Pedido[], factorDe: FactorDe, excepto?: string | null): Map<string, Milli> {
  const apartado = new Map<string, Milli>();
  for (const p of pedidos) {
    if (p.estado !== 'pendiente' || p.id === excepto) continue;
    for (const [productoId, cantidad] of loQuePide(p.lineas, factorDe)) {
      apartado.set(productoId, addQty(apartado.get(productoId) ?? ZERO_QTY, cantidad));
    }
  }
  return apartado;
}

/** What is left to sell or to promise: what the shop holds less what is set aside, never below nothing. */
export const libre = (hay: Milli, apartado: Milli): Milli => (hay > apartado ? subQty(hay, apartado) : ZERO_QTY);

/** One product an order cannot be given in full: how much it asks for, and how much of it is missing. */
export interface Faltante {
  productoId: string;
  pide: Milli;
  falta: Milli;
}

/**
 * Which pending orders the shop's stock still covers: the stock goes to the orders in the order
 * they were taken, and each order that does not get all it asks for is listed with what it lacks.
 * Covered orders are not in the result.
 */
export function cobertura(pedidos: readonly Pedido[], factorDe: FactorDe, hayDe: (productoId: string) => Milli): Map<string, Faltante[]> {
  const queda = new Map<string, Milli>();
  const cortos = new Map<string, Faltante[]>();
  for (const p of [...pedidos].filter((x) => x.estado === 'pendiente').sort(primeroElPrimero)) {
    for (const [productoId, pide] of loQuePide(p.lineas, factorDe)) {
      const hay = queda.get(productoId) ?? (hayDe(productoId) > 0 ? hayDe(productoId) : ZERO_QTY);
      if (pide > hay) {
        cortos.set(p.id, [...(cortos.get(p.id) ?? []), { productoId, pide, falta: subQty(pide, hay) }]);
        queda.set(productoId, ZERO_QTY);
      } else {
        queda.set(productoId, subQty(hay, pide));
      }
    }
  }
  return cortos;
}
